import ELK from "elkjs/lib/elk.bundled.js";

import type { ElkNode, ElkExtendedEdge } from "elkjs/lib/elk-api";

import { updateBoundElements } from "./binding";
import { getMindmapChildren, getMindmapParent, isMindmapNode } from "./mindmap";
import { getBoundTextElement } from "./textElement";
import type { ElementsMap, ExcalidrawElement } from "./types";

import type { Scene } from "./Scene";

const elk = new ELK();

/**
 * Find the root node of a mind map tree by traversing up from any node.
 */
export const findMindmapRoot = (
  node: ExcalidrawElement,
  elementsMap: ElementsMap,
): ExcalidrawElement => {
  let current = node;
  let parent = getMindmapParent(current, elementsMap);
  while (parent) {
    current = parent;
    parent = getMindmapParent(current, elementsMap);
  }
  return current;
};

/**
 * Build an ELK graph from a mind map tree.
 */
const buildElkGraph = (
  rootNode: ExcalidrawElement,
  elementsMap: ElementsMap,
): ElkNode => {
  const children: ElkNode[] = [];
  const edges: ElkExtendedEdge[] = [];

  const traverse = (node: ExcalidrawElement) => {
    children.push({
      id: node.id,
      width: node.width,
      height: node.height,
    });

    // Sort children by Y position to preserve visual order
    const nodeChildren = getMindmapChildren(node, elementsMap)
      .sort((a, b) => a.y - b.y);

    for (const child of nodeChildren) {
      edges.push({
        id: `edge-${node.id}-${child.id}`,
        sources: [node.id],
        targets: [child.id],
      });
      traverse(child);
    }
  };

  traverse(rootNode);

  return {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "RIGHT",
      "elk.spacing.nodeNode": "40",
      "elk.layered.spacing.nodeNodeBetweenLayers": "80",
      "elk.edgeRouting": "POLYLINE",
      // Preserve the input order of nodes (sorted by Y) instead of optimizing crossings
      "elk.layered.crossingMinimization.strategy": "NONE",
    },
    children,
    edges,
  };
};

/**
 * Apply ELK layout positions to mind map elements.
 * Keeps the root node at its current position and offsets all others relative to it.
 */
const applyElkLayout = (
  elkGraph: ElkNode,
  rootNode: ExcalidrawElement,
  elementsMap: ElementsMap,
  scene: Scene,
) => {
  if (!elkGraph.children) {
    return;
  }

  const rootElkNode = elkGraph.children.find((n) => n.id === rootNode.id);
  if (!rootElkNode || rootElkNode.x == null || rootElkNode.y == null) {
    return;
  }

  // Offset so the root stays at its original position
  const offsetX = rootNode.x - rootElkNode.x;
  const offsetY = rootNode.y - rootElkNode.y;

  for (const elkNode of elkGraph.children) {
    if (elkNode.x == null || elkNode.y == null) {
      continue;
    }

    const element = elementsMap.get(elkNode.id);
    if (!element || element.isDeleted) {
      continue;
    }

    const newX = elkNode.x + offsetX;
    const newY = elkNode.y + offsetY;

    // Only move if position actually changed
    if (Math.abs(element.x - newX) > 0.5 || Math.abs(element.y - newY) > 0.5) {
      // Calculate delta BEFORE mutating (element.x changes after mutation)
      const dx = newX - element.x;
      const dy = newY - element.y;

      scene.mutateElement(element, {
        x: newX,
        y: newY,
      });

      // Move bound text element (e.g. text inside rectangle) with the same delta
      const boundText = getBoundTextElement(element, elementsMap);
      if (boundText) {
        scene.mutateElement(boundText, {
          x: boundText.x + dx,
          y: boundText.y + dy,
        });
      }

      // Update all arrows connected to this node
      updateBoundElements(element, scene);
    }
  }
};

/**
 * Calculate and apply ELK layout to an entire mind map tree.
 * The root node stays at its current position; all other nodes are repositioned.
 */
export const layoutMindmapTree = async (
  rootNode: ExcalidrawElement,
  elementsMap: ElementsMap,
  scene: Scene,
): Promise<void> => {
  if (!isMindmapNode(rootNode)) {
    return;
  }

  const graph = buildElkGraph(rootNode, elementsMap);

  // Need at least 2 nodes for layout to make sense
  if (!graph.children || graph.children.length < 2) {
    return;
  }

  const layoutedGraph = await elk.layout(graph);
  applyElkLayout(layoutedGraph, rootNode, elementsMap, scene);
};
